import { NetGainByScheduledPayementDateOutput } from '@allocab/allocab-invoice-service-types';
import dayjs from 'dayjs';
import { useState } from 'react';

import { TransactionInvoicesListModal } from './TransactionInvoicesListModal';
import { TransactionStateBadge } from './TransactionStateBadge';
import { getPaymentLabel, getPaymentText, getRejectionReason } from './utils';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { useFetchIncomingTransactions } from '../../hooks/useFetchIncomingTransactions';
import { TRANSACTION_LIMIT } from '../../hooks/useFetchTransactions';
import { t } from '../../services/translation/translation';
import { formatDate } from '../../utils/date';
import { formatPrice } from '../../utils/price';

export const IncomingTransactionList = () => {
  const { data, isLoading, isFetching, isPreviousData } = useFetchIncomingTransactions();
  const nbPages = Math.ceil((data?.totalItems ?? 0) / TRANSACTION_LIMIT);
  const enrichedData = data?.items.map(item => ({
    ...item,
    key: `${item.scheduledPaymentDate}-${item.rejectedAt ?? 'no-reject'}-${item.data.netGain.amount}`,
  }));

  const columns: TableColumn<NetGainByScheduledPayementDateOutput>[] = [
    {
      name: t('common.date'),
      render: transaction => (
        <div className="inline-flex flex-col items-start">
          <span className="text-sm opacity-50">
            {transaction.rejectedAt ? t('transaction.date.blocked') : getPaymentLabel(transaction)}
          </span>
          <span className="text-sm">
            {transaction.rejectedAt
              ? formatDate(transaction.rejectedAt, t('date.date-longer'))
              : getPaymentText(transaction)}
          </span>
        </div>
      ),
    },
    {
      name: t('common.status'),
      render: transaction => (
        <TransactionStateBadge
          status={
            transaction.rejectedAt
              ? 'REJECTED'
              : transaction.paymentInformations.from &&
                dayjs().tz('Europe/Paris').isAfter(dayjs(transaction.paymentInformations.from))
              ? 'CURRENT'
              : 'IN_PROCESS'
          }
        />
      ),
    },
    {
      name: t('common.amount'),
      render: transaction => (
        <span className="font-bold text-sm text-green-500">{formatPrice(transaction.data.netGain.amount)}</span>
      ),
    },
    {
      name: t('transaction.rejection.reason'),
      render: transaction =>
        transaction.rejectedAt && transaction.rejectionReason ? (
          <div className="inline-flex items-center space-x-2">
            <span className="text-sm opacity-50">
              {t('transaction.reason', {
                date: formatDate(transaction.rejectedAt, t('date.date-longer')),
              })}
            </span>
            <span className="text-sm text-red-500">{getRejectionReason(transaction.rejectionReason)}</span>
          </div>
        ) : null,
    },
    {
      name: t('common.invoices'),
      render: transaction => (
        <div>
          <div
            onClick={() => setSelectedTransaction(transaction.invoiceNumbers)}
            className="text-sm text-blue-500 cursor-pointer"
          >
            {t('transaction.invoices-list')}
          </div>
        </div>
      ),
    },
  ];

  const [selectedTransaction, setSelectedTransaction] = useState<string[]>([]);

  return (
    <div>
      <Table
        columns={columns}
        data={enrichedData}
        identifier="key"
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
      />
      {selectedTransaction.length > 0 ? (
        <TransactionInvoicesListModal
          onClose={() => setSelectedTransaction([])}
          selectedTransaction={selectedTransaction}
        />
      ) : null}
    </div>
  );
};
